import { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createFragmentContainer, graphql } from 'react-relay';

import * as tracking from 'dibs-tracking';
import { pageTypeConstants as PAGE_TYPES, pageTypeConstants } from '../constants/pageTypeConstants';
import { getPageviewData } from '../utils/tracking/searchBrowse/getPageviewData';

import { SharedUserAndRelayVarLoader } from '../shared/SharedUserAndRelayVarLoader';
import { handlePageRefetchVariableOnUserChange } from '../shared/handlePageRefetchVariableOnUserChange';

import { updateRelayVariables } from '../actions/sharedRelayVariableActions';
import { toggleRequestInFlight, updateUriRef } from '../actions/filterActions';

import { getNewPaginationUriRef, addCdnQueryParams } from '../utils/uriUtils';

import { FindingRouter } from '../router/FindingRouter';
import {
    itemRegionOnTileTracking,
    itemTileShorterHeightTracking,
    personalizedRerankTracking,
} from '../finding/SbSharedTrackingFunctions/SbSharedTrackingFunctions';
import { trackEligibleForRerankEvent } from '../utils/tracking/searchBrowse/addPersonalizationTrackingData';

import { handleSbUserCountryCode } from '../finding/helpers/handleSbUserCountryCode';

class BuySharedRefetchContainerComponent extends Component {
    constructor() {
        super();

        this.handleUserRefetch = this.handleUserRefetch.bind(this);
        this.handleBuyPageRefetch = this.handleBuyPageRefetch.bind(this);
        this.refetchCallback = this.refetchCallback.bind(this);
        this.handlePageChange = this.handlePageChange.bind(this);

        this.hasFiredEligibleForRerankEvent = createRef();
    }

    componentDidMount() {
        const { buyPage } = this.props;
        const {
            pageType,
            totalResults,
            appliedFilters,
            displayUriRef,
            appliedPersonalizationList,
        } = buyPage;
        this.buyPageName = appliedFilters.find(
            f => f.name === PAGE_TYPES.BUY
        )?.values?.[0]?.urlLabel;
        this.router = new FindingRouter({ routes: [{ path: '/buy/:pageName?' }] });
        this.props.updateUriRef({ uriRef: displayUriRef });
        tracking.trackPageview({
            additional: {
                ...getPageviewData({
                    pageType,
                    totalResults,
                    appliedFilters,
                }),
                dimension143: appliedPersonalizationList?.length
                    ? appliedPersonalizationList
                    : null,
            },
        });

        personalizedRerankTracking({
            pageType: pageTypeConstants.BUY,
            sortValue: buyPage?.sort?.currentOption?.urlLabel,
        });
        itemRegionOnTileTracking();
        itemTileShorterHeightTracking();
    }

    componentDidUpdate(prevProps) {
        const { generatedUriRef, page: newPage, viewer } = this.props;
        const oldUriRef = addCdnQueryParams(prevProps.generatedUriRef);
        const newUriRef = addCdnQueryParams(generatedUriRef);
        const uriRefChanged = oldUriRef !== '' && newUriRef !== oldUriRef;
        const pageChanged = newPage !== prevProps.page;
        let uriRef = generatedUriRef;

        if (pageChanged) {
            uriRef = getNewPaginationUriRef(generatedUriRef, newPage);
            this.handlePageChange(newPage);
        }

        if (uriRefChanged) {
            const variables = { uriRef, page: newPage };
            this.props.toggleRequestInFlight(true);
            this.handleBuyPageRefetch(
                { ...variables, fetchBuy: true, skipUser: true },
                null,
                this.refetchCallback
            );
            this.props.updateRelayVariables(variables);
        }

        if (viewer?.isEligibleForRerank && !this.hasFiredEligibleForRerankEvent.current) {
            trackEligibleForRerankEvent();
            this.hasFiredEligibleForRerankEvent.current = true;
        }
    }

    refetchCallback() {
        this.props.toggleRequestInFlight(false);
        this.props.updateUriRef({ uriRef: this.props?.buyPage?.displayUriRef });
    }

    handlePageChange(page) {
        if (this.buyPageName) {
            this.router.navigate(
                getNewPaginationUriRef(`/${PAGE_TYPES.BUY}/${this.buyPageName}/`, page),
                { replace: true }
            );
        }
    }

    handleUserRefetch(variables, renderVariables, callback) {
        const { buyPage, relayVariables } = this.props;
        const { displayUriRef } = buyPage || {};
        const { followSearchPages } = relayVariables || {};
        //Personalized-rerank needs to refetch s&b after login
        const fetchBuy =
            handlePageRefetchVariableOnUserChange(variables) || variables.fetchSbOnUserChange;
        this.handleBuyPageRefetch(
            {
                ...variables,
                fetchBuy,
                skipUser: false,
                followSearchPages: displayUriRef ? [displayUriRef] : followSearchPages,

                userCountryCode: handleSbUserCountryCode({
                    userCountryCode: variables?.userCountryCode,
                    fetchSb: variables?.fetchSb,
                }),
            },
            renderVariables,
            callback
        );
    }

    handleBuyPageRefetch(variables, renderVariables, callback) {
        this.props.refetch(variables, renderVariables, callback);
    }

    render() {
        return <SharedUserAndRelayVarLoader onUserRefetch={this.handleUserRefetch} />;
    }
}

function mapStateToProps(state) {
    const { filters, relayVariables } = state;
    const { generatedUriRef, page } = filters;

    return {
        page,
        generatedUriRef,
        relayVariables: relayVariables.variables,
    };
}

const mapDispatchToProps = {
    toggleRequestInFlight,
    updateUriRef,
    updateRelayVariables,
};

BuySharedRefetchContainerComponent.propTypes = {
    refetch: PropTypes.func.isRequired,
    generatedUriRef: PropTypes.string,
    toggleRequestInFlight: PropTypes.func.isRequired,
    updateUriRef: PropTypes.func.isRequired,
    page: PropTypes.number.isRequired,
    updateRelayVariables: PropTypes.func.isRequired,
    buyPage: PropTypes.object,
    viewer: PropTypes.object,
    relayVariables: PropTypes.shape({
        followSearchPages: PropTypes.arrayOf(PropTypes.string),
        userCountryCode: PropTypes.string,
        priceBookName: PropTypes.string,
    }).isRequired,
};

export const BuySharedRefetchContainer = createFragmentContainer(
    connect(mapStateToProps, mapDispatchToProps)(BuySharedRefetchContainerComponent),
    {
        viewer: graphql`
            fragment BuySharedRefetchContainer_viewer on Viewer {
                isEligibleForRerank(userId: $rerankUserId, guestId: $rerankGuestId)
                    @include(if: $isClient)
            }
        `,
        buyPage: graphql`
            fragment BuySharedRefetchContainer_buyPage on ItemSearchQueryConnection {
                pageType
                totalResults
                appliedFilters {
                    name
                    values {
                        displayName
                        urlLabel
                        code
                    }
                }
                sort {
                    currentOption {
                        urlLabel
                    }
                }
                displayUriRef
                appliedPersonalizationList
            }
        `,
    }
);
